import React from 'react';
import { SECTION_SHAPES, type SectionShapeType } from '../../utils/sectionShapes';

interface SectionBackgroundProps {
  config: any;
}

export const SectionBackground: React.FC<SectionBackgroundProps> = ({ config }) => {
  const {
    backgroundType,
    backgroundImage,
    backgroundVideo,
    overlayOpacity,
    overlayMode,
    overlay,
    topDivider,
    bottomDivider
  } = config;
  const opacity = overlayOpacity !== undefined ? Math.min(0.9, Math.max(0.1, overlayOpacity)) : 0.5;
  const mode = overlayMode || 'full';
  const isVideo = backgroundType === 'video' && backgroundVideo;
  const showOverlay = isVideo ? overlay === true : overlay !== false;

  const renderDivider = (position: 'top' | 'bottom') => {
    const dividerConfig = position === 'top' ? topDivider : bottomDivider;
    if (!dividerConfig || !dividerConfig.enabled) return null;

    const shapeData = SECTION_SHAPES[dividerConfig.shape as SectionShapeType] || SECTION_SHAPES.wave;

        return (
            <div
                className={`absolute left-0 right-0 w-full overflow-hidden leading-none z-[1] pointer-events-none ${position === 'top' ? 'top-[-1px] rotate-180' : 'bottom-[-1px]'
                    }`}
        style={{ height: dividerConfig.height || '100px' }}
      >
        <svg
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox={shapeData.viewBox}
          preserveAspectRatio={shapeData.preserveAspectRatio}
          className={`relative block w-[calc(100%+1.3px)] h-full ${dividerConfig.flip ? 'scale-x-[-1]' : ''}`}
          style={{ fill: dividerConfig.color && dividerConfig.color !== '#' ? dividerConfig.color : 'currentColor' }}
        >
          <path d={shapeData.path}></path>
        </svg>
      </div>
    );
  };

  const getOverlayStyle = (): React.CSSProperties => {
    if (mode === 'left') {
      return { background: `linear-gradient(90deg, rgba(0,0,0,${opacity}) 0%, rgba(0,0,0,0) 68%)` };
    }
    if (mode === 'right') {
      return { background: `linear-gradient(270deg, rgba(0,0,0,${opacity}) 0%, rgba(0,0,0,0) 68%)` };
    }
    if (mode === 'sides') {
      return {
        background: `linear-gradient(90deg, rgba(0,0,0,${opacity}) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,${opacity}) 100%)`
      };
    }
    return { backgroundColor: `rgba(0, 0, 0, ${opacity})` };
  };

  const Content = () => (
    <>
      {showOverlay && <div className="absolute inset-0" style={getOverlayStyle()} />}
      {renderDivider('top')}
      {renderDivider('bottom')}
    </>
  );

    if (backgroundType === 'video' && backgroundVideo) {
        return (
            <div className="absolute inset-0 z-[2] overflow-hidden pointer-events-none">
                <video
                    src={backgroundVideo}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        />
        <Content />
      </div>
    );
  }

    // An image with no backgroundType still counts. Seed data and older saved
    // sections carry `backgroundImage` alone, and sections keyed off the URL
    // rather than the type — CTASection lays a dark scrim "whenever an image URL
    // is present" — so requiring the type here meant the scrim rendered over
    // nothing. On Verdure that was the grey band across the CTA banner: a
    // configured photo that never painted, dimmed by a scrim for the photo.
    // An explicit non-image type still wins, so a leftover URL under
    // backgroundType: 'color' stays unpainted.
    if ((backgroundType === 'image' || !backgroundType) && backgroundImage) {
        return (
            <div
                className="absolute inset-0 z-[2] bg-cover bg-center pointer-events-none"
                style={{ backgroundImage: `url(${backgroundImage})` }}
            >
        <Content />
      </div>
    );
  }

    if (topDivider?.enabled || bottomDivider?.enabled) {
        return (
            <div className="absolute inset-0 z-[2] pointer-events-none">
                {renderDivider('top')}
                {renderDivider('bottom')}
            </div>
    );
  }

  return null;
};
